import React from 'react';

import debounce from 'lodash/debounce';

import KeyboardArrowLeftIcon from '@edna/icons/keyboardArrowLeft.svg';
import KeyboardArrowRightIcon from '@edna/icons/keyboardArrowRight.svg';

import {DEFAULT_DEBOUNCE_DELAY} from 'src/constants';

import * as S from './style';

type TProps = {
  gap?: number;
  children: React.ReactNode;
};

type TItem = {
  width: number;
  left: number;
  right: number;
};

const COPY_COUNT = 3;
const SCROLL_COMPENSATOR = 5;
const BORDER_COMPENSATOR = 1;

export const InfiniteCarousel = React.memo<TProps>(({children, gap = 20}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const viewportRef = React.useRef<HTMLDivElement>(null);
  const isAnimatingRef = React.useRef(false);
  const originalItemsWidthRef = React.useRef<number>(0);
  const [hasOverflow, setOverflow] = React.useState<boolean>(false);
  const itemsRef = React.useRef<TItem[]>([]);

  const getOriginalCopyWidth = React.useCallback(() => {
    if (!itemsRef.current.length || !!originalItemsWidthRef.current) {
      return originalItemsWidthRef.current;
    }

    if (!hasOverflow) {
      return viewportRef.current?.scrollWidth ?? 0;
    }

    const originalCount = itemsRef.current.length / COPY_COUNT;
    const originalLeft = itemsRef.current[originalCount].left;
    const originalRight = itemsRef.current[itemsRef.current.length - originalCount - 1].right;

    return originalRight - originalLeft;
  }, [hasOverflow]);

  const normalizeScroll = React.useCallback(() => {
    if (!viewportRef.current || !originalItemsWidthRef.current || isAnimatingRef.current) {
      return;
    }
    const width = originalItemsWidthRef.current + gap;
    const scroll = viewportRef.current.scrollLeft;

    if (scroll < width + SCROLL_COMPENSATOR) {
      viewportRef.current.scrollLeft += width;
    } else if (scroll > (width + SCROLL_COMPENSATOR) * 2) {
      viewportRef.current.scrollLeft -= width;
    }
  }, []);

  const measure = React.useMemo(
    () =>
      debounce(() => {
        if (!viewportRef.current) {
          return;
        }

        const items = [...viewportRef.current.children] as HTMLDivElement[];

        itemsRef.current = items.map((item) => ({
          width: item.clientWidth,
          left: item.offsetLeft,
          right: item.offsetLeft + item.clientWidth,
        }));

        const originalCopyWidth = getOriginalCopyWidth();

        originalItemsWidthRef.current = originalCopyWidth;

        const isOverflow = originalCopyWidth > viewportRef.current.clientWidth;

        setOverflow(isOverflow);

        if (!isOverflow) {
          return;
        }

        if (!viewportRef.current.scrollLeft) {
          viewportRef.current.scrollLeft = originalCopyWidth + gap - BORDER_COMPENSATOR;
        }

        normalizeScroll();
      }, DEFAULT_DEBOUNCE_DELAY),
    [hasOverflow],
  );

  React.useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollLeft = 0;
    }

    originalItemsWidthRef.current = 0;

    measure();
  }, [children]);

  React.useEffect(() => {
    if (viewportRef.current) {
      viewportRef.current.scrollLeft = 0;
    }

    measure();
  }, [hasOverflow]);

  React.useLayoutEffect(() => {
    if (!viewportRef.current) {
      return;
    }

    const resizeObserver = new ResizeObserver(measure);

    for (const child of viewportRef.current.children) {
      resizeObserver.observe(child);
    }

    window.addEventListener('resize', measure);
    window.addEventListener('scroll', normalizeScroll);

    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', normalizeScroll);
      resizeObserver.disconnect();
    };
  }, []);

  const move = React.useCallback(
    (direction: 1 | -1) => () => {
      if (!viewportRef.current) {
        return;
      }

      const isNext = direction > 0;
      const viewportLeft = viewportRef.current.scrollLeft + SCROLL_COMPENSATOR * direction;
      const viewportRight = viewportLeft + viewportRef.current.clientWidth;

      let targetLeft = 0;

      if (isNext) {
        const nextItem = itemsRef.current.find((item) => item.right > viewportRight);

        targetLeft = (nextItem?.right ?? 0) - viewportRef.current.clientWidth + BORDER_COMPENSATOR;
      } else {
        const prevItem = [...itemsRef.current].reverse().find((item) => item.left < viewportLeft);

        targetLeft = (prevItem?.left ?? 0) - BORDER_COMPENSATOR;
      }

      isAnimatingRef.current = true;

      viewportRef.current.scrollTo({
        left: targetLeft,
        behavior: 'smooth',
      });

      setTimeout(() => {
        isAnimatingRef.current = false;
        normalizeScroll();
      }, DEFAULT_DEBOUNCE_DELAY);
    },
    [],
  );

  if (!hasOverflow) {
    return (
      <S.Container ref={containerRef}>
        <S.Items ref={viewportRef} gap={gap}>
          {children}
        </S.Items>
      </S.Container>
    );
  }

  return (
    <S.Container ref={containerRef}>
      <S.Button position="left" onClick={move(-1)}>
        <KeyboardArrowLeftIcon />
      </S.Button>
      <S.Items ref={viewportRef} gap={gap} onWheel={normalizeScroll}>
        {children}
        {children}
        {children}
      </S.Items>
      <S.Button position="right" onClick={move(1)}>
        <KeyboardArrowRightIcon />
      </S.Button>
    </S.Container>
  );
});

InfiniteCarousel.displayName = 'InfiniteCarousel';
